import {
  Button,
  Col,
  Input,
  Row,
  Segmented,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPlatformPermissionsMatrix,
  setPlatformPermissionCell,
} from "../repository/data.repository";
import {
  PermissionAction,
  PermissionMatrix,
} from "../repository/data.default";
import { getProblemMessage, retry } from "../utils/http";

export interface PermissionsMatrixProps {
  readonly trigger?: string;
}

// Admin brief 2026-11-04 — role × page permission matrix. Three visual states
// per cell: "—" (None, no row), "View", "Edit". Edit maps to Delete=400 on the
// wire so the cell grants full CRUD. Writes are immediate (per-cell PUT) to
// avoid "Save all" inconsistency.
function PermissionsMatrix(_props: PermissionsMatrixProps) {
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    retry(() => getPlatformPermissionsMatrix())
      .then((m) => setMatrix(m))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load matrix."),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Build a fast (roleUid, permissionUid) -> action index so the cell render
  // doesn't do an O(cells) scan per row × role.
  const cellIndex = useMemo(() => {
    const idx = new Map<string, PermissionAction>();
    (matrix?.cells ?? []).forEach((c) =>
      idx.set(`${c.roleUid}|${c.permissionUid}`, c.allowedAction),
    );
    return idx;
  }, [matrix]);

  const filteredPermissions = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return matrix?.permissions ?? [];
    return (matrix?.permissions ?? []).filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q),
    );
  }, [matrix, filter]);

  async function onCellChange(
    roleUid: string,
    permissionUid: string,
    newState: "none" | "view" | "edit",
  ): Promise<void> {
    const key = `${roleUid}|${permissionUid}`;
    const action =
      newState === "none"
        ? PermissionAction.None
        : newState === "view"
          ? PermissionAction.View
          : PermissionAction.Delete; // "Edit" grants full CRUD
    setSavingKey(key);
    try {
      await setPlatformPermissionCell(roleUid, permissionUid, action);
      // Optimistic local update so the matrix doesn't flash on every click.
      setMatrix((m) => {
        if (!m) return m;
        const next = m.cells.filter(
          (c) => !(c.roleUid === roleUid && c.permissionUid === permissionUid),
        );
        if (action !== PermissionAction.None) {
          next.push({ roleUid, permissionUid, allowedAction: action });
        }
        return { ...m, cells: next };
      });
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to save cell.");
    } finally {
      setSavingKey(null);
    }
  }

  if (loading && !matrix) {
    return (
      <Col className="flex items-center justify-center p-6">
        <Spin />
      </Col>
    );
  }
  if (!matrix) return null;

  const columns = [
    {
      title: "Page",
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      width: 240,
      render: (v: string, row: (typeof matrix.permissions)[number]) => (
        <Tooltip title={row.description}>
          <span>{v}</span>
        </Tooltip>
      ),
    },
    ...matrix.roles.map((role) => ({
      title: shortRoleName(role.name),
      key: role.uid,
      width: 150,
      align: "center" as const,
      render: (_: unknown, perm: (typeof matrix.permissions)[number]) => {
        const action = cellIndex.get(`${role.uid}|${perm.uid}`) ?? PermissionAction.None;
        const current: "none" | "view" | "edit" =
          action === PermissionAction.None
            ? "none"
            : action <= PermissionAction.View
              ? "view"
              : "edit";
        const key = `${role.uid}|${perm.uid}`;
        return (
          <Segmented
            size="small"
            value={current}
            disabled={savingKey === key}
            onChange={(v) => onCellChange(role.uid, perm.uid, v as any)}
            options={[
              { label: "—", value: "none" },
              { label: "View", value: "view" },
              { label: "Edit", value: "edit" },
            ]}
          />
        );
      },
    })),
  ];

  return (
    <Col className="flex flex-auto flex-col gap-2 overflow-hidden">
      <Row align="middle" justify="space-between" className="px-1">
        <Space>
          <Typography.Text type="secondary" className="text-xs">
            Click a cell to grant access. Changes save instantly. "Edit"
            implies View + Create + Update + Delete.
          </Typography.Text>
        </Space>
        <Space>
          <Input.Search
            allowClear
            size="small"
            style={{ width: 220 }}
            placeholder="Filter pages…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </Space>
      </Row>
      <Table
        size="small"
        rowKey={(r) => r.uid}
        dataSource={filteredPermissions as any}
        columns={columns as any}
        pagination={false}
        scroll={{ x: 240 + matrix.roles.length * 150, y: 600 }}
      />
    </Col>
  );
}

// Trim the "App" prefix for the column header so "AppCompanyAdmin" renders as
// "Company Admin" — easier to scan on a wide matrix.
function shortRoleName(name: string): string {
  const stripped = name.startsWith("App") ? name.slice(3) : name;
  return stripped
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/User$/, "")
    .trim();
}

export default PermissionsMatrix;
